import type { Decoration, ResolvedCardTheme } from './cardTheme';
import { applyCurrentColor, sanitizeSvg } from './svgUtils';

/**
 * Sprint BI Part 2 — Decoration Rasterizer (SVG → PNG dataURL)。
 *
 * 旧 `old/apps/admin/src/features/cardPrint/decorationRasterizer.ts` を L2 で移植。
 *
 * 用途:
 *   - 装飾 SVG を card cell の絶対座標位置に焼き付けるための bitmap 生成
 *   - native `window.print()` 経由でも、SVG → `<img src=data:...>` で印刷品質を確保
 *   - jsPDF/html2canvas 依存を新規導入せず、canvas + toDataURL のみで完結 (browser 標準)
 *
 * 旧との差分:
 *   - 旧 `tintWith: 'primary' | 'secondary' | 'none'` → 本 sprint は `'accent' | 'none'` に簡略化
 *     (M7 theme は primary/secondary を持たず accent 1 色のみ、Phase 5 後続で拡張)
 *   - cache キーに `svgSource.length` + 先頭 200 文字 + tint + 寸法 を採用 (旧 同等)
 *
 * テスト容易性:
 *   - Image / canvas を internally 参照するため、jsdom テストでは `rasterizeDecoration`
 *     の主要 path をモックなしで unit test 困難。
 *   - 本 module の test は `buildCacheKey` などの pure helper を export して行う。
 *   - `rasterizeDecoration` 自体は smoke (cache hit / sanitize fail) のみ test。
 */

const CSS_PX_PER_MM = 96 / 25.4;
const RASTER_SCALE = 4;

const cache = new Map<string, string>();

export function buildCacheKey(decoration: Decoration, tint: string): string {
  return `${decoration.svgSource.length}|${decoration.width_mm}x${decoration.height_mm}|${tint}|${decoration.svgSource.slice(0, 200)}`;
}

/**
 * decoration.tintWith に基づき theme から具体的な color を解決。
 * - 'accent' / 'primary' → theme.accent (old2 の primary は v2 の accent と同義)
 * - 'secondary' → theme.secondary
 * - 'none' / undefined → '#000000' (currentColor が SVG 内にない場合は影響なし)
 */
export function resolveTintColor(
  decoration: Decoration,
  theme: Pick<ResolvedCardTheme, 'accent' | 'secondary'>,
): string {
  if (decoration.tintWith === 'accent' || decoration.tintWith === 'primary') return theme.accent;
  if (decoration.tintWith === 'secondary') return theme.secondary;
  return '#000000';
}

function loadSvgImage(svgSource: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    if (typeof Image === 'undefined' || typeof URL === 'undefined' || typeof Blob === 'undefined') {
      reject(new Error('Image / URL / Blob 未対応の環境です'));
      return;
    }
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('SVG raster load failed'));
    const blob = new Blob([svgSource], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    img.src = url;
    img.addEventListener('load', () => setTimeout(() => URL.revokeObjectURL(url), 50), {
      once: true,
    });
  });
}

/**
 * SVG 装飾を PNG data URL に rasterize。
 * 失敗時 (sanitize 失敗 / Image load 失敗 / canvas 取得失敗) は null。
 * 同一 decoration + tint の組み合わせは cache 命中で即座に返す。
 */
export async function rasterizeDecoration(
  decoration: Decoration,
  theme: Pick<ResolvedCardTheme, 'accent' | 'secondary'>,
): Promise<string | null> {
  const tint = resolveTintColor(decoration, theme);
  const cacheKey = buildCacheKey(decoration, tint);
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  const cleaned = sanitizeSvg(decoration.svgSource);
  if (!cleaned) return null;
  const colored = applyCurrentColor(cleaned, tint);

  const widthPx = Math.max(1, Math.round(decoration.width_mm * CSS_PX_PER_MM * RASTER_SCALE));
  const heightPx = Math.max(1, Math.round(decoration.height_mm * CSS_PX_PER_MM * RASTER_SCALE));

  let img: HTMLImageElement;
  try {
    img = await loadSvgImage(colored);
  } catch {
    return null;
  }

  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.clearRect(0, 0, widthPx, heightPx);
  ctx.drawImage(img, 0, 0, widthPx, heightPx);
  const dataUrl = canvas.toDataURL('image/png');
  cache.set(cacheKey, dataUrl);
  return dataUrl;
}

export function clearDecorationCache(): void {
  cache.clear();
}

/**
 * Decoration を 1 枚の card cell 内に配置するための inline style を返す。
 *
 * 旧の DecorationSlot ごとの (x, y, width, height) 自動配置は scope 外 (Phase 5 後続)。
 * 本 sprint は decoration.{x_mm, y_mm, width_mm, height_mm} を絶対座標で採用。
 */
export function decorationStyle(decoration: Decoration): React.CSSProperties {
  return {
    position: 'absolute',
    left: `${decoration.x_mm}mm`,
    top: `${decoration.y_mm}mm`,
    width: `${decoration.width_mm}mm`,
    height: `${decoration.height_mm}mm`,
    pointerEvents: 'none',
  };
}
